/**
 * Workplace module survey values for Excel export - numeric codes (not display labels).
 * Aligns with SURVEY_SCALE_HEADERS and optional choice order in StudyModule.js.
 *
 * Likert (awareness before/after, experience): stored and exported as 0-5
 *   0 = N/A, 1 = SD, 2 = D, 3 = NAD, 4 = A, 5 = SA
 *
 * Optional EDI identity selections: exported as 0-based indices (semicolon-separated).
 * optional.identityOther stays as text.
 */

import { WORKPLACE_EDI_IDENTITY_VALUE_ORDER } from '../data/workplaceSurveyIdentityOptions';

/** @type {Record<number, string>} Reference for analysts - not written to export cells */
export const WORKPLACE_SURVEY_LIKERT_BY_VALUE = {
  0: 'Not applicable',
  1: 'Strongly disagree',
  2: 'Disagree',
  3: 'Neither agree nor disagree',
  4: 'Agree',
  5: 'Strongly agree',
};

const LIKERT_KEY_PREFIXES = ['awarenessBefore.', 'awarenessAfter.', 'experience.'];

/** Option value order per field (must match StudyModule optional choice UI order). */
const OPTIONAL_FIELD_OPTION_ORDER = {
  followupInterview: ['yes', 'no'],
  currentRole: [
    'teamLead',
    'managerSupervisor',
    'seniorLeaderExecutive',
    'informalLeaderMentorChangeAgent',
    'other',
    'preferNotToSay',
  ],
  yearsExperience: ['under5', '6to10', '11to15', '16plus', 'preferNotToSay'],
  workContext: [
    'scienceResearch',
    'technologyDigital',
    'engineering',
    'mathAccountingFinanceAnalytics',
    'medicineHealth',
    'educationAcademia',
    'nonProfitCommunity',
    'publicSectorGovernment',
    'privateSectorCorporate',
    'other',
    'preferNotToSay',
  ],
};

const OPTIONAL_TEXT_FIELDS = new Set(['identityOther', 'currentRoleOther', 'workContextOther']);

/**
 * @param {unknown} value
 * @returns {number | ''}
 */
function likertToExportCode(value) {
  if (value === '' || value === null || value === undefined) return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '';
  return n;
}

/**
 * @param {string} field
 * @param {unknown} value
 * @returns {number | string | '' | undefined}
 */
/**
 * @param {unknown} value
 * @returns {string}
 */
function identitySelectionsToExportCode(value) {
  if (value === null || value === undefined || value === '') return '';
  let keys = [];
  if (Array.isArray(value)) {
    keys = value.map((item) => String(item).trim()).filter(Boolean);
  } else {
    const s = String(value).trim();
    if (!s) return '';
    keys = s.split(/\s*;\s*/).map((item) => item.trim()).filter(Boolean);
  }
  const indices = keys
    .map((key) => WORKPLACE_EDI_IDENTITY_VALUE_ORDER.indexOf(key))
    .filter((idx) => idx >= 0);
  return indices.length ? indices.join('; ') : '';
}

function optionalFieldToExportCode(field, value) {
  if (OPTIONAL_TEXT_FIELDS.has(field)) {
    if (value === null || value === undefined) return '';
    return String(value);
  }
  if (field === 'identitySelections') {
    return identitySelectionsToExportCode(value);
  }
  const order = OPTIONAL_FIELD_OPTION_ORDER[field];
  if (!order || value === null || value === undefined || value === '') return '';
  const key = String(value).trim();
  const idx = order.indexOf(key);
  return idx >= 0 ? idx : '';
}

/**
 * @param {Record<string, string | number | boolean>} row - flattened row (pre-column relabel)
 * @returns {Record<string, string | number | boolean>}
 */
export function encodeWorkplaceSurveyExportRow(row) {
  const out = { ...row };
  for (const [key, value] of Object.entries(row)) {
    if (LIKERT_KEY_PREFIXES.some((p) => key.startsWith(p))) {
      const code = likertToExportCode(value);
      if (code !== undefined) out[key] = code;
      continue;
    }
    if (key.startsWith('optional.')) {
      const field = key.slice('optional.'.length);
      const encoded = optionalFieldToExportCode(field, value);
      if (encoded !== undefined) out[key] = encoded;
    }
  }
  return out;
}

/** @deprecated Use encodeWorkplaceSurveyExportRow - kept for any legacy callers */
export function decodeWorkplaceSurveyFlatRow(row) {
  return encodeWorkplaceSurveyExportRow(row);
}
